import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Paper,
  Typography,
  Chip,
  Popover,
  List,
  ListItem,
  ListItemText,
  InputBase,
  keyframes,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Custom Dropdown Components
export const CustomDropdownContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  minWidth: '150px',

  // Tablet styles
  [theme.breakpoints.between('sm', 'lg')]: {
    width: '100%',
  },
}));

export const CustomDropdownButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '25px',
  padding: theme.spacing(0.5, 2),
  boxShadow: 'none',
  textTransform: 'none',
  minWidth: '150px',
  height: '40px',
  textAlign: 'left',
  justifyContent: 'space-between',
  border: '1px solid #D3D3D3',
  color: '#575758',
  fontSize: '14px',
  fontFamily: 'Prompt, sans-serif',

  // Tablet styles
  [theme.breakpoints.between('sm', 'lg')]: {
    width: '100%',
    height: '44px',
    borderRadius: '40px',
    fontSize: '16px',
    padding: '12px 24px',
  },

  '&:hover': {
    backgroundColor: '#f5f5f5',
    border: '1px solid #F4762A',
    boxShadow: 'none',
  },
  '&:focus': {
    backgroundColor: '#f5f5f5',
    border: '1px solid #F4762A',
  },
}));

export const CustomDropdownMenu = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 1000,
  borderRadius: '8px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
  marginTop: '4px',
  maxHeight: '300px',
  overflow: 'auto',
  backgroundColor: 'white',

  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#D3D3D3',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#F4762A',
  },
}));

export const CustomDropdownMenuItem = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  cursor: 'pointer',
  borderBottom: '1px solid #f0f0f0',
  fontSize: '14px',
  fontFamily: 'Prompt, sans-serif',

  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '&:last-child': {
    borderBottom: 'none',
  },
  '&.selected': {
    backgroundColor: '#FEF1EA',
    color: '#F4762A',
    fontWeight: 500,
  },
}));

// Filter Dropdown Components
export const FilterDropdownContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
}));

export const FilterDropdownButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '25px',
  padding: theme.spacing(0.5, 2),
  boxShadow: 'none',
  textTransform: 'none',
  minWidth: '120px',
  height: '40px',
  textAlign: 'left',
  justifyContent: 'flex-start',
  border: '1px solid #D3D3D3',
  color: '#575758',
  fontSize: '14px',
  fontFamily: 'Prompt, sans-serif',

  // Tablet styles
  [theme.breakpoints.between('sm', 'lg')]: {
    width: '100%',
    height: '44px',
    borderRadius: '40px',
    fontSize: '16px',
    padding: '12px 24px',
  },

  '&:hover': {
    backgroundColor: '#f5f5f5',
    border: '1px solid #F4762A',
    boxShadow: 'none',
  },
  '&:focus': {
    backgroundColor: '#f5f5f5',
    border: '1px solid #F4762A',
  },
}));

export const FilterDropdownMenu = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  zIndex: 1000,
  borderRadius: '12px',
  boxShadow: '0px 5px 25px rgba(0, 0, 0, 0.15)',
  marginTop: '4px',
  minWidth: '350px',
  maxHeight: '500px',
  overflow: 'hidden',
  backgroundColor: 'white',
  padding: '16px',
}));

// Search Dropdown Components
export const SearchDropdownContainer = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 1000,
  borderRadius: '8px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
  marginTop: '4px',
  maxHeight: '400px',
  overflow: 'hidden',
  backgroundColor: 'white',
}));

export const SearchDropdownList = styled(List)(({ theme }) => ({
  padding: 0,
  maxHeight: '400px',
  overflow: 'auto',

  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#D3D3D3',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#F4762A',
  },
}));

export const SearchDropdownItem = styled(ListItem)(({ theme }) => ({
  padding: '12px 16px',
  cursor: 'pointer',
  borderBottom: '1px solid #f0f0f0',
  
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '&:last-child': {
    borderBottom: 'none',
  },
}));

export const SearchDropdownItemText = styled(ListItemText)(({ theme }) => ({
  '& .MuiListItemText-primary': {
    fontSize: '14px',
    fontFamily: 'Prompt, sans-serif',
    color: '#333',
  },
  '& .MuiListItemText-secondary': {
    fontSize: '12px',
    fontFamily: 'Prompt, sans-serif',
    color: '#666',
  },
}));

// Location Dropdown Components
export const LocationDropdownContainer = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 1000,
  borderRadius: '8px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
  marginTop: '4px',
  maxHeight: '300px',
  overflow: 'hidden',
  backgroundColor: 'white',
}));

export const LocationDropdownList = styled(List)(({ theme }) => ({
  padding: 0,
  maxHeight: '300px',
  overflow: 'auto',

  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#D3D3D3',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#F4762A',
  },
}));

export const LocationDropdownItem = styled(ListItem)(({ theme }) => ({
  padding: '12px 16px',
  cursor: 'pointer',
  borderBottom: '1px solid #f0f0f0',

  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '&:last-child': {
    borderBottom: 'none',
  },
}));

// Filter Section Components
export const FilterSection = styled(Box)(({ theme }) => ({
  marginBottom: '16px',
}));

export const FilterSectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  fontFamily: 'Prompt, sans-serif',
  color: '#333',
  marginBottom: '8px',
}));

export const FilterOptionGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
  gap: '8px',
  marginBottom: '12px',
}));

export const FilterOptionButton = styled(Button)(({ theme }) => ({
  minHeight: '36px',
  borderRadius: '18px',
  border: 'none',
  backgroundColor: 'white',
  color: '#666',
  fontSize: '12px',
  fontFamily: 'Prompt, sans-serif',
  textTransform: 'none',
  padding: '6px 12px',

  '&:hover': {
    backgroundColor: '#f5f5f5',
    border: 'none',
  },

  '&.selected': {
    backgroundColor: '#F4762A',
    color: 'white',
    border: 'none',

    '&:hover': {
      backgroundColor: '#e06521',
    },
  },
}));

export const PriceRangeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  marginBottom: '12px',
}));

export const PriceRangeInput = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    height: '36px',
    fontSize: '12px',
    fontFamily: 'Prompt, sans-serif',

    '& fieldset': {
      borderColor: '#D3D3D3',
    },
    '&:hover fieldset': {
      borderColor: '#F4762A',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#F4762A',
    },
  },
  '& .MuiInputBase-input': {
    padding: '8px 12px',
  },
}));

export const PriceRangeSeparator = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: '#666',
  fontFamily: 'Prompt, sans-serif',
}));

// Action Buttons
export const FilterActionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '8px',
  marginTop: '16px',
  paddingTop: '16px',
  borderTop: '1px solid #f0f0f0',
}));

export const FilterResetButton = styled(Button)(({ theme }) => ({
  flex: 1,
  borderRadius: '20px',
  border: '1px solid #D3D3D3',
  backgroundColor: 'white',
  color: '#666',
  fontSize: '12px',
  fontFamily: 'Prompt, sans-serif',
  textTransform: 'none',
  height: '36px',

  '&:hover': {
    backgroundColor: '#f5f5f5',
    border: '1px solid #666',
  },
}));

export const FilterApplyButton = styled(Button)(({ theme }) => ({
  flex: 1,
  borderRadius: '20px',
  backgroundColor: '#F4762A',
  color: 'white',
  fontSize: '12px',
  fontFamily: 'Prompt, sans-serif',
  textTransform: 'none',
  height: '36px',

  '&:hover': {
    backgroundColor: '#e06521',
  },
}));

// Loading and Empty States
export const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px',
  fontSize: '14px',
  color: '#666',
  fontFamily: 'Prompt, sans-serif',
}));

export const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px',
  fontSize: '14px',
  color: '#666',
  fontFamily: 'Prompt, sans-serif',
}));

// Utility Components
export const Backdrop = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 999,
  backgroundColor: 'transparent',
}));

export const HighlightText = styled('span')(({ theme }) => ({
  backgroundColor: '#FFEB3B',
  fontWeight: 'bold',
}));

// Responsive Helpers
export const DesktopOnly = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}));

export const TabletOnly = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

export const MobileOnly = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}));

// FilterDropdown Components from backup-rebase
export const FilterContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  width: 'auto',
  [theme.breakpoints.down('lg')]: {
    width: '100%',
  },
}));

export const FilterContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  right: 0,
  background: 'white',
  borderRadius: '20px',
  zIndex: 1000,
  width: '752px',
  maxHeight: '600px',
  overflow: 'hidden',
  animation: `${fadeIn} 0.3s ease-in-out`,

  [theme.breakpoints.between('sm', 'lg')]: {
    width: '490px',
  },
  [theme.breakpoints.down('xs')]: {
    display: 'none',
  },


}));

export const FilterButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '25px',
  padding: '8px 16px',
  boxShadow: 'none',
  textTransform: 'none',
  minWidth: '259px',
  height: '40px',
  textAlign: 'left',
  justifyContent: 'flex-start',
  border: '1px solid #D3D3D3',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: 'Prompt, sans-serif',
  fontSize: '16px',
  color: '#333',
  width: '100% !important',
  fontWeight: 400,

  [theme.breakpoints.between('md', 'lg')]: {
    minWidth: '150px',
    width: '100% !important',
  },

  [theme.breakpoints.down('sm')]: {
    display: 'none',
    width: '100%',
  },

  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#F4762A',
  },
}));

export const ScrollableContent = styled(Box)(({ theme }) => ({
  maxHeight: '540px',
  overflowY: 'auto',
  padding: '20px',

  '&::-webkit-scrollbar': {
    width: '1px',
    borderRadius: '10px',
  },

  '&::-webkit-scrollbar-track': {
    background: 'transparent !important',
  },

  '&::-webkit-scrollbar-thumb': {
    background: '#575758',
    borderRadius: '10px',
  },

  scrollbarWidth: 'thin',
  scrollbarColor: '#575758 transparent',
}));

export const ActionButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '10px',
  padding: '20px',
  borderTop: '1px solid #f0f0f0',
  background: 'white',
}));

export const ActionButton = styled(Button)(({ theme }) => ({
  flex: 1,
  padding: '12px 24px',
  borderRadius: '25px',
  fontFamily: 'Prompt, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  textTransform: 'none',

  '&.outlined': {
    background: 'white',
    color: '#333',
    border: '1px solid #D3D3D3',

    '&:hover': {
      background: '#f5f5f5',
      borderColor: '#F4762A',
      color: '#F4762A',
    },
  },

  '&.contained': {
    background: '#F4762A',
    color: 'white',
    border: '1px solid #F4762A',

    '&:hover': {
      background: '#e06521',
      borderColor: '#e06521',
    },
  },
}));

// Price Section Components
export const PriceInput = styled(InputBase)(({ theme }) => ({
  width: '100%',
  height: '43px',
  borderRadius: '40px',
  padding: '12px 24px',
  paddingRight: '50px', // Add space for clear button
  border: '1px solid #D3D3D3',
  background: '#FFFFFF',
  fontFamily: 'Prompt, sans-serif',
  fontSize: '16px',
  fontWeight: 400,

  '&:hover, &.Mui-focused': {
    border: '1px solid #F4762A',
  },

  '& .MuiInputAdornment-root': {
    position: 'absolute',
    right: '12px',
  },

  '& .MuiIconButton-root': {
    padding: '4px',
    '&:hover': {
      backgroundColor: 'rgba(244, 118, 42, 0.1)',
    },
  },
}));

export const PriceInputContainer = styled(Box)(({ theme }) => ({
  marginBottom: '8px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
}));

export const PriceButtonScrollContainer = styled(Box)(({ theme }) => ({
  maxHeight: '120px',
  overflowY: 'auto',

  [theme.breakpoints.down('sm')]: {
    maxHeight: '200px',
  },

  '&::-webkit-scrollbar': {
    width: '1px',
    borderRadius: '10px',
  },

  '&::-webkit-scrollbar-track': {
    background: 'transparent !important',
  },

  '&::-webkit-scrollbar-thumb': {
    background: '#575758',
    borderRadius: '10px',
  },

  scrollbarWidth: 'thin',
  scrollbarColor: '#575758 transparent',
}));

export const PriceButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  padding: '12px',
  textTransform: 'none',
  color: '#575758',
  backgroundColor: '#ffffff',
  fontSize: '16px',
  fontWeight: 400,
  fontFamily: 'Prompt, sans-serif',
  width: '100%',
  height: 'auto',
  display: 'flex',
  justifyContent: 'flex-start',
  border: 'none',
  cursor: 'pointer',

  '&:hover': {
    backgroundColor: '#f5f5f5',
    border: '1px solid #F4762A',
  },
}));

export const Divider = styled(Box)(({ theme }) => ({
  height: '1px',
  backgroundColor: '#f0f0f0',
  margin: '8px 0',
}));

// Filter Accordion Components
export const FilterAccordion = styled(Box)(({ theme }) => ({
  boxShadow: 'none',
  backgroundColor: 'transparent',
  transition: 'all 0.3s ease',

  '&.expanded': {
    margin: '4px 0',
    borderRadius: '8px',
    backgroundColor: '#FEF1EA10',
  },
}));

export const FilterAccordionSummary = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  [theme.breakpoints.down('sm')]: {
    padding: '0',
  },

  '&.expanded': {
    minHeight: '48px',
    backgroundColor: '#FEF1EA',
    borderRadius: '8px',
  },

  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
  },

  '& .expand-icon': {
    transition: 'transform 0.3s ease-in-out',
    color: '#666666',
  },

  '&.expanded .expand-icon': {
    transform: 'rotate(180deg)',
    color: '#F4762A',
  },

  '& .section-title': {
    fontWeight: 500,
    transition: 'color 0.2s ease',
    color: '#919192',
  },

  '&.expanded .section-title': {
    color: '#F4762A',
    fontWeight: 600,
  },
}));

export const FilterAccordionDetails = styled(Box)(({ theme }) => ({
  padding: '16px 16px',
  maxHeight: '270px',
  overflowY: 'auto',
  marginTop: '8px',
  background: '#fff',
  borderRadius: '8px',

  [theme.breakpoints.down('sm')]: {
    padding: '0',
    maxHeight: 'none',
  },

  '&::-webkit-scrollbar': {
    width: '1px',
    borderRadius: '10px',
  },

  '&::-webkit-scrollbar-track': {
    background: 'transparent !important',
  },

  '&::-webkit-scrollbar-thumb': {
    background: '#575758',
    borderRadius: '10px',
  },

  scrollbarWidth: 'thin',
  scrollbarColor: '#575758 transparent',
}));

// Bedroom Section Components
export const BedroomButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
}));

export const BedroomButton = styled(Button)(({ theme }) => ({
  minWidth: '48px',
  height: '42px',
  borderRadius: '21px',
  padding: '8px 16px',
  textTransform: 'none',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 400,
  fontFamily: 'Prompt, sans-serif',
  transition: 'all 0.2s ease',
  border: '1px solid #D3D3D3',
  backgroundColor: '#ffffff',
  color: '#575758',

  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#F4762A',
  },

  '&.selected': {
    backgroundColor: '#F4762A',
    color: 'white',
    borderColor: '#F4762A',
  },
}));

// Area Section Components  
export const AreaInputContainer = styled(Box)(({ theme }) => ({
  marginBottom: '8px',
}));

export const AreaInput = styled(InputBase)(({ theme }) => ({
  width: '100%',
  height: '43px',
  borderRadius: '40px',
  padding: '12px 24px',
  border: '1px solid #D3D3D3',
  background: '#FFFFFF',
  fontFamily: 'Prompt, sans-serif',
  fontSize: '16px',
  fontWeight: 400,

  '&:hover, &.Mui-focused': {
    border: '1px solid #F4762A',
  },
}));

// Generic Filter Components
export const GenericFilterButton = styled(Button)(({ theme }) => ({
  minWidth: '80px',
  height: '42px',
  borderRadius: '21px',
  padding: '8px 16px',
  textTransform: 'none',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 400,
  fontFamily: 'Prompt, sans-serif',
  transition: 'all 0.2s ease',
  border: '1px solid #D3D3D3',
  backgroundColor: '#ffffff',
  color: '#575758',
  margin: '4px',

  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#F4762A',
  },

  '&.selected': {
    backgroundColor: '#F4762A',
    color: 'white',
    borderColor: '#F4762A',
  },
}));

export const GenericFilterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
}));

// Specific Filter Button Components
export const BathroomButton = styled(Button)(({ theme }) => ({
  minWidth: '48px',
  height: '42px',
  borderRadius: '21px',
  padding: '8px 16px',
  textTransform: 'none',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 400,
  fontFamily: 'Prompt, sans-serif',
  transition: 'all 0.2s ease',
  border: '1px solid #D3D3D3',
  backgroundColor: '#ffffff',
  color: '#575758',
  width: '100%',
  marginBottom: '8px',

  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#F4762A',
  },

  '&.selected': {
    backgroundColor: '#F4762A',
    color: 'white',
    borderColor: '#F4762A',
  },
}));

export const QuotaButton = styled(Button)(({ theme }) => ({
  minWidth: '48px',
  height: '42px',
  borderRadius: '21px',
  padding: '8px 16px',
  textTransform: 'none',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 400,
  fontFamily: 'Prompt, sans-serif',
  transition: 'all 0.2s ease',
  border: '1px solid #D3D3D3',
  backgroundColor: '#ffffff',
  color: '#575758',
  width: '100%',
  marginBottom: '8px',

  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#F4762A',
  },

  '&.selected': {
    backgroundColor: '#F4762A',
    color: 'white',
    borderColor: '#F4762A',
  },
}));

export const StatusButton = styled(Button)(({ theme }) => ({
  minWidth: '48px',
  height: '42px',
  borderRadius: '21px',
  padding: '8px 16px',
  textTransform: 'none',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 400,
  fontFamily: 'Prompt, sans-serif',
  transition: 'all 0.2s ease',
  border: '1px solid #D3D3D3',
  backgroundColor: '#ffffff',
  color: '#575758',
  width: '100%',
  marginBottom: '8px',

  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#F4762A',
  },

  '&.selected': {
    backgroundColor: '#F4762A',
    color: 'white',
    borderColor: '#F4762A',
  },
}));

export const FloorButton = styled(Button)(({ theme }) => ({
  minWidth: '48px',
  height: '42px',
  borderRadius: '21px',
  padding: '8px 16px',
  textTransform: 'none',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 400,
  fontFamily: 'Prompt, sans-serif',
  transition: 'all 0.2s ease',
  border: '1px solid #D3D3D3',
  backgroundColor: '#ffffff',
  color: '#575758',
  width: '100%',
  marginBottom: '8px',

  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#F4762A',
  },

  '&.selected': {
    backgroundColor: '#F4762A',
    color: 'white',
    borderColor: '#F4762A',
  },
}));

export const ViewButton = styled(Button)(({ theme }) => ({
  minWidth: '48px',
  height: '42px',
  borderRadius: '21px',
  padding: '8px 16px',
  textTransform: 'none',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 400,
  fontFamily: 'Prompt, sans-serif',
  transition: 'all 0.2s ease',
  border: '1px solid #D3D3D3',
  backgroundColor: '#ffffff',
  color: '#575758',
  width: '100%',
  marginBottom: '8px',

  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#F4762A',
  },

  '&.selected': {
    backgroundColor: '#F4762A',
    color: 'white',
    borderColor: '#F4762A',
  },
}));

export const DirectionButton = styled(Button)(({ theme }) => ({
  minWidth: '48px',
  height: '42px',
  borderRadius: '21px',
  padding: '8px 16px',
  textTransform: 'none',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 400,
  fontFamily: 'Prompt, sans-serif',
  transition: 'all 0.2s ease',
  border: '1px solid #D3D3D3',
  backgroundColor: '#ffffff',
  color: '#575758',
  width: '100%',
  marginBottom: '8px',

  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#F4762A',
  },

  '&.selected': {
    backgroundColor: '#F4762A',
    color: 'white',
    borderColor: '#F4762A',
  },
}));

export const PublishedByButton = styled(Button)(({ theme }) => ({
  minWidth: '48px',
  height: '42px',
  borderRadius: '21px',
  padding: '8px 16px',
  textTransform: 'none',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 400,
  fontFamily: 'Prompt, sans-serif',
  transition: 'all 0.2s ease',
  border: '1px solid #D3D3D3',
  backgroundColor: '#ffffff',
  color: '#575758',
  width: '100%',
  marginBottom: '8px',

  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#F4762A',
  },

  '&.selected': {
    backgroundColor: '#F4762A',
    color: 'white',
    borderColor: '#F4762A',
  },
}));

export const PostedDateButton = styled(Button)(({ theme }) => ({
  minWidth: '48px',
  height: '42px',
  borderRadius: '21px',
  padding: '8px 16px',
  textTransform: 'none',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 400,
  fontFamily: 'Prompt, sans-serif',
  transition: 'all 0.2s ease',
  border: '1px solid #D3D3D3',
  backgroundColor: '#ffffff',
  color: '#575758',
  width: '100%',
  marginBottom: '8px',

  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#F4762A',
  },

  '&.selected': {
    backgroundColor: '#F4762A',
    color: 'white',
    borderColor: '#F4762A',
  },
})); 